import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { MapPin, Clock, Star, Calendar, User, MessageCircle, Flag, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { RatingStars } from '../components/common/RatingStars';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import ReportModal from '../components/common/ReportModal';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ServiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, api } = useAuth();
  const [service, setService] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    requested_date: '',
    duration_hours: 1,
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchService();
    fetchReviews();
  }, [id]);

  const fetchService = async () => {
    try {
      const response = await axios.get(`${API_URL}/services/${id}`);
      setService(response.data);
    } catch (error) {
      console.error('Error fetching service:', error);
      toast.error('Service non trouvé');
      navigate('/services');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API_URL}/reviews/service/${id}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const creditsNeeded = bookingForm.duration_hours * service.credits_per_hour;
    if (user.credits_balance < creditsNeeded) {
      toast.error(`Crédits insuffisants. Il vous faut ${creditsNeeded} crédits.`);
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/bookings', {
        service_id: id,
        ...bookingForm,
      });
      toast.success('Demande de réservation envoyée !');
      setBookingModalOpen(false);
      setBookingForm({ requested_date: '', duration_hours: 1, message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réservation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReport = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/reports', {
        reported_service_id: id,
        reason: reportReason,
      });
      toast.success('Signalement envoyé');
      setReportModalOpen(false);
      setReportReason('');
    } catch (error) {
      toast.error('Erreur lors du signalement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleContact = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    navigate(`/messages?user=${service.provider?.id}`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!service) return null;

  const creditsTotal = bookingForm.duration_hours * service.credits_per_hour;
  const isOwnService = user?.id === service.provider?.id;

  return (
    <Layout>
      <div className="container-app py-8 md:py-12">
        {/* Back Button */}
        <Link
          to="/services"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour aux services
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Service Image/Icon */}
            <div className="relative aspect-video rounded-2xl overflow-hidden bg-muted">
              {service.image_url ? (
                <img
                  src={service.image_url}
                  alt={service.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-primary/5">
                  <span className="text-8xl">{service.category?.icon || '✨'}</span>
                </div>
              )}
              <Badge className="absolute top-4 left-4 bg-background/80 backdrop-blur-sm text-foreground border-0">
                {service.category?.icon} {service.category?.name}
              </Badge>
            </div>

            {/* Title & Description */}
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-4" data-testid="service-title">
                {service.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-muted-foreground mb-6">
                <span className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4" />
                  {service.location}
                </span>
                {service.rating_count > 0 && (
                  <span className="flex items-center gap-1.5">
                    <Star className="w-4 h-4 text-secondary fill-secondary" />
                    {service.rating_average.toFixed(1)} ({service.rating_count} avis)
                  </span>
                )}
              </div>
              <p className="text-lg leading-relaxed whitespace-pre-wrap">
                {service.description}
              </p>
            </div>

            {/* Reviews */}
            <Card className="rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="w-5 h-5" />
                  Avis ({reviews.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {reviews.length > 0 ? (
                  <div className="space-y-4">
                    {reviews.map((review) => (
                      <div key={review.id} className="border-b border-border pb-4 last:border-0 last:pb-0">
                        <div className="flex items-start gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={review.reviewer?.avatar_url} />
                            <AvatarFallback className="bg-primary/10 text-primary">
                              {review.reviewer?.full_name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium">{review.reviewer?.full_name}</span>
                              <span className="text-sm text-muted-foreground">
                                {format(new Date(review.created_at), 'dd MMM yyyy', { locale: fr })}
                              </span>
                            </div>
                            <RatingStars rating={review.rating} showCount={false} size="sm" />
                            {review.comment && (
                              <p className="text-muted-foreground mt-2">{review.comment}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">
                    Aucun avis pour le moment
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Booking Card */}
            <Card className="rounded-2xl sticky top-24">
              <CardContent className="p-6">
                {/* Price */}
                <div className="flex items-center justify-between mb-6 pb-6 border-b border-border">
                  <span className="text-muted-foreground">Coût</span>
                  <div className="credit-badge text-lg">
                    <Clock className="w-5 h-5 text-secondary" />
                    <span className="font-bold">{service.credits_per_hour}</span>
                    <span className="text-muted-foreground">/ heure</span>
                  </div>
                </div>

                {/* Provider */}
                <Link
                  to={`/users/${service.provider?.id}`}
                  className="flex items-center gap-3 mb-6 p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
                >
                  <Avatar className="h-14 w-14">
                    <AvatarImage src={service.provider?.avatar_url} />
                    <AvatarFallback className="bg-primary/10 text-primary text-lg">
                      {service.provider?.full_name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{service.provider?.full_name}</span>
                      {service.provider?.verified && (
                        <CheckCircle className="w-4 h-4 text-primary" />
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      {service.provider?.rating_count > 0 && (
                        <span className="flex items-center gap-1">
                          <Star className="w-3.5 h-3.5 text-secondary fill-secondary" />
                          {service.provider?.rating_average?.toFixed(1)}
                        </span>
                      )}
                      <span>{service.provider?.total_exchanges} échanges</span>
                    </div>
                  </div>
                </Link>

                {/* Actions */}
                {!isOwnService && (
                  <div className="space-y-3">
                    <Dialog open={bookingModalOpen} onOpenChange={setBookingModalOpen}>
                      <DialogTrigger asChild>
                        <Button className="w-full btn-primary" data-testid="book-btn">
                          <Calendar className="w-4 h-4 mr-2" />
                          Réserver
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Réserver ce service</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleBooking} className="space-y-4 mt-4">
                          <div className="space-y-2">
                            <Label htmlFor="date">Date souhaitée *</Label>
                            <Input
                              id="date"
                              type="datetime-local"
                              value={bookingForm.requested_date}
                              onChange={(e) => setBookingForm({ ...bookingForm, requested_date: e.target.value })}
                              required
                              className="rounded-xl"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="duration">Durée (heures) *</Label>
                            <Input
                              id="duration"
                              type="number"
                              min="1"
                              value={bookingForm.duration_hours}
                              onChange={(e) => setBookingForm({ ...bookingForm, duration_hours: parseFloat(e.target.value) })}
                              required
                              className="rounded-xl"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="message">Message (optionnel)</Label>
                            <Textarea
                              id="message"
                              value={bookingForm.message}
                              onChange={(e) => setBookingForm({ ...bookingForm, message: e.target.value })}
                              placeholder="Précisez vos besoins..."
                              className="rounded-xl"
                            />
                          </div>

                          <div className="p-4 bg-muted rounded-xl">
                            <div className="flex items-center justify-between">
                              <span>Coût total</span>
                              <div className="credit-badge">
                                <Clock className="w-4 h-4 text-secondary" />
                                <span className="font-bold">{creditsTotal}</span>
                              </div>
                            </div>
                            {user && (
                              <p className="text-sm text-muted-foreground mt-2">
                                Votre solde : {user.credits_balance} crédits
                              </p>
                            )}
                          </div>

                          <Button
                            type="submit"
                            className="w-full btn-primary"
                            disabled={submitting}
                          >
                            {submitting ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : null}
                            Envoyer la demande
                          </Button>
                        </form>
                      </DialogContent>
                    </Dialog>

                    <Button
                      variant="outline"
                      className="w-full rounded-full"
                      onClick={handleContact}
                      data-testid="contact-btn"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Contacter
                    </Button>

                    <Button variant="ghost" className="w-full text-muted-foreground" onClick={() => setReportModalOpen(true)}>
                      <Flag className="w-4 h-4 mr-2" />
                      Signaler
                    </Button>
                  </div>
                )}

                {isOwnService && (
                  <Link to={`/dashboard`}>
                    <Button className="w-full" variant="outline">
                      Gérer ce service
                    </Button>
                  </Link>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      {/* Report Modal */}
      <ReportModal
        isOpen={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        type="service"
        targetId={id}
        api={api}
      />
    </Layout>
  );
}
